import { isEmptyObject } from '@/helpers/object'
import { removeFromArray } from '@/helpers/array'

export const OVERLAY_IDS = {
  addPreviousYear: 'addPreviousYear',
  writeMessageForm: 'writeMessageForm',
  infoQuestion: 'infoQuestion',
  message: 'message',
  messageYear2020: 'messageYear2020',
  mobileMenu: 'mobileMenu',
  proceedOrNot: 'proceedOrNot',
  termsOfUse: 'termsOfUse',
  homeVideo: 'homeVideo',
  survey: 'survey',
  cookieConsent: 'cookieConsent',
  privacySettings: 'privacySettings',
  adminStatus: 'adminStatus',
  emailForReminder: 'emailForReminder',
  gallery: 'gallery',
  noticeOfAssessmentAdvice: 'noticeOfAssessmentAdvice',
}

export const OVERLAY_COMPONENT_NAMES = {
  [OVERLAY_IDS.addPreviousYear]: 'OverlayAddPreviousYear',
  [OVERLAY_IDS.writeMessageForm]: 'OverlayWriteMessageForm',
  [OVERLAY_IDS.infoQuestion]: 'OverlayInfoQuestion',
  [OVERLAY_IDS.message]: 'OverlayMessage',
  [OVERLAY_IDS.messageYear2020]: 'OverlayMessageYear2020',
  [OVERLAY_IDS.mobileMenu]: 'OverlayMobileMenu',
  [OVERLAY_IDS.proceedOrNot]: 'OverlayProceedOrNot',
  [OVERLAY_IDS.termsOfUse]: 'OverlayTermsOfUse',
  [OVERLAY_IDS.homeVideo]: 'OverlayHomeVideo',
  [OVERLAY_IDS.survey]: 'OverlaySurvey',
  [OVERLAY_IDS.cookieConsent]: 'OverlayCookieConsent',
  [OVERLAY_IDS.privacySettings]: 'OverlayPrivacySettings',
  [OVERLAY_IDS.adminStatus]: 'OverlayAdminStatus',
  [OVERLAY_IDS.emailForReminder]: 'OverlayEmailForReminder',
  [OVERLAY_IDS.gallery]: 'OverlayGallery',
  [OVERLAY_IDS.noticeOfAssessmentAdvice]: 'OverlayNoticeOfAssessmentAdvice',
}

// Return true if it is not a full screen overlay
const NON_FULL_SCREEN_OVERLAY_IDS = {
  [OVERLAY_IDS.survey]: () => true,
  [OVERLAY_IDS.cookieConsent]: ({ isForcingToAnswer }) => !isForcingToAnswer,
  [OVERLAY_IDS.emailForReminder]: () => true,
}

export const URL_PARAMS_TO_SHOW_OVERLAYS = {}

export const isValidId = value => Object.values(OVERLAY_IDS).includes(value)

/**
 * A show, hide, and preload action are auto-generated for each OVERLAY_IDS. The action name is
 * suffixed by the component name.
 *
 * @example
 * OVERLAY_IDS.infoQuestion has showOverlayInfoQuestion(props) and preloadOverlayInfoQuestion()
 */
const AUTO_GENERATED_ACTIONS = {
  show: 'show',
  hide: 'hide',
  preload: 'preload',
}

function getActionFunction(action, id) {
  const functions = {
    [AUTO_GENERATED_ACTIONS.show]({ dispatch }, props = {}) {
      dispatch('show', { id, props })
    },
    [AUTO_GENERATED_ACTIONS.hide]({ dispatch }) {
      dispatch('hide', { id })
    },
    [AUTO_GENERATED_ACTIONS.preload]({ dispatch }) {
      dispatch('preload', { id })
    },
  }
  return functions[action]
}

function generateActionForEachOverlayIds(store) {
  Object.values(AUTO_GENERATED_ACTIONS).forEach(action => {
    for (const key in OVERLAY_IDS) {
      const actionName = `${action}${OVERLAY_COMPONENT_NAMES[key]}`
      store.actions[actionName] = getActionFunction(action, OVERLAY_IDS[key])
    }
  })
}

function getIdFromPayload(payload) {
  return payload.id || payload
}

const storeSettings = {
  namespaced: true,
  state: () => ({
    props: {},

    /**
     * @property {String[]} preloadedOverlayIds
     * @property {String[]} visibleOverlayIds
     * @property {String[]} renderedOverlayIds
     * @property {String[]} leavingOverlayIds
     *
     * List of overlay ids for a state. First of all, an overlay is added to renderedOverlayIds
     * from the show action which renders the overlay component in OverlayLoader.vue. The ids are
     * only added to visibleOverlayIds from Overlay.vue to be able to control the visibility from
     * the specific overlay component (i.e. having an init delay).
     *
     * We need two different states since we have to render the component to know if it will wait
     * before showing the overlay, but only prevent the page from scrolling if the overlay is
     * visible.
     *
     * Finally, the hide action will add the id to leavingOverlayIds which will fade out the
     * component and remove the id from all 3 state arrays on after-leave. We use a different state
     * to be able to rely on the fact that if an id is included in visibleOverlayIds, it means
     * it is still visible even when it's fading out.
     */
    preloadedOverlayIds: [],
    renderedOverlayIds: [],
    visibleOverlayIds: [],
    leavingOverlayIds: [],
  }),

  mutations: {
    addOverlayToPreloadedIds(state, id) {
      if (!isValidId(id)) return

      if (!state.preloadedOverlayIds.includes(id)) state.preloadedOverlayIds.push(id)
    },

    setProps(state, payload) {
      const props = { ...payload.props }
      if (isValidId(payload.id) && !isEmptyObject(props)) {
        state.props[payload.id] = props
      }
    },

    setOverlayVisibility(state, payload) {
      if (!isValidId(payload.id)) return

      removeFromArray(state.visibleOverlayIds, payload.id)
      if (payload.isVisible) state.visibleOverlayIds.push(payload.id)
    },

    setOverlayRenderedState(state, payload) {
      if (!isValidId(payload.id)) return

      // If the targeted overlay is already rendered, remove it from the renderedOverlayIds
      // and push it back into the array to have it at the end which means it is the one
      // visible on top of all the visible overlays.
      removeFromArray(state.renderedOverlayIds, payload.id)
      if (payload.isRendered) state.renderedOverlayIds.push(payload.id)
    },

    setLeavingOverlayIds(state, payload) {
      if (payload.isLeaving && !state.leavingOverlayIds.includes(payload.id)) {
        state.leavingOverlayIds.push(payload.id)
      } else if (!payload.isLeaving) {
        removeFromArray(state.leavingOverlayIds, payload.id)
      }
    },
  },

  actions: {
    /**
     * Show an overlay based on its id.
     *
     * @name show
     * @function
     * @param {object} context - store context
     * @param {string|object} payload - the id itself or an object containing the id and props
     * @param {string} payload.id - a valid overlay id
     * @param {object} payload.props - an object setting the value of the props (optional)
     */
    show({ commit }, payload) {
      const id = getIdFromPayload(payload)
      if (payload.props) commit('setProps', payload)

      commit('setOverlayVisibility', { id, isVisible: false })
      commit('setLeavingOverlayIds', { id, isLeaving: false })
      commit('setOverlayRenderedState', { id, isRendered: true })
    },

    hide({ commit }, payload) {
      const id = getIdFromPayload(payload)
      commit('setLeavingOverlayIds', { id, isLeaving: true })
    },

    preload({ commit }, payload) {
      const id = getIdFromPayload(payload)
      commit('addOverlayToPreloadedIds', id)
    },
  },

  getters: {
    hasFullScreenVisibleOverlays: state =>
      !!state.visibleOverlayIds.filter(id => {
        return id in NON_FULL_SCREEN_OVERLAY_IDS
          ? !NON_FULL_SCREEN_OVERLAY_IDS[id](state.props[id] || {})
          : true
      }).length,
  },
}
generateActionForEachOverlayIds(storeSettings)

export default storeSettings
